import Phaser from 'phaser';
import { GAME_FONT_FAMILY, WIKI_URL } from '@shared/constants';
import {
  SKILL_DEFS,
  ALL_SKILL_IDS,
  getSkillLevelFromXp,
  getXpToNextLevel,
  getSkillWikiUrl,
  SKILL_MAX_LEVEL,
} from '@shared/data/skills';
import type { SkillId, SkillCategory } from '@shared/types/skills';
import { applyIconToDom, createUIIcon, formatThousands } from '../../sprites/IconAtlases';

/**
 * Escena del juego, con el helper de filtrado de cámara que expone GameScene
 * (`configureObjectCamera`): pone un objeto en la uiCamera (depth ≥ 800) para
 * que la cámara principal lo ignore. Los pines lo usan al crearse.
 */
type HudScene = Phaser.Scene & {
  configureObjectCamera(obj: Phaser.GameObjects.GameObject): void;
};

/**
 * Panel de Skills estilo RuneScape (OSRS): grilla COMPACTA de 3 columnas, celdas
 * solo-ícono + nivel + barra de XP sutil abajo. Toda la data (nombre, XP, %) se ve
 * en un TOOLTIP con fade al hover, que además trae las acciones (wiki / favorito /
 * pin). Header con "Nivel total". Ventana flotante draggable, clampeada al canvas
 * del juego (no entra en las barras negras del letterbox), con posición persistida.
 * Toggle con tecla K. Ver docs/SKILLS_SYSTEM.md.
 */

/** Color de acento (borde izquierdo de la celda) por tipo de habilidad. */
const CATEGORY_COLOR: Record<SkillCategory, string> = {
  combat: '#c8553d', // rojo — combate
  gathering: '#5aa46a', // verde — extracción
  processing: '#5a86c8', // azul — procesamiento
  support: '#a069c8', // violeta — utilidad
};

const FAV_KEY = 'aureum.skillFavorites';
const PIN_KEY = 'aureum.skillPins';
const POS_KEY = 'aureum.skillPanelPos';
const MAX_PINS = 3;
/** Distancia (px) a la que un pin arrastrado "salta" para pegarse al borde de otro. */
const PIN_SNAP_PX = 12;

// ── Pines como HUD Phaser (como el minimapa) ─────────────────────────────────
// Se dibujan sobre el CANVAS (no DOM), en la uiCamera a un depth por DEBAJO de
// las ventanas del juego (Inventario/Character empiezan en 850), así cualquier
// ventana que se abra los tapa naturalmente, sin ocultarlos por código.
const PIN_W = 92;
const PIN_H = 46;
const PIN_DEPTH = 830;
const PIN_BG_COLOR = 0x14100a;
const PIN_BG_ALPHA = 0.94;
const PIN_BORDER_COLOR = 0xcaa24a;
const PIN_BAR_BG_COLOR = 0x0a0803;
const PIN_FILL_COLOR = 0x8fd46a;
const PIN_FILL_MAX_COLOR = 0xe0c064;
const PIN_LEVEL_COLOR = '#f2d98a';
const PIN_RADIUS = 6;
const PIN_PAD = 8; // margen interno horizontal (barra/ícono/nivel)
const PIN_BAR_H = 7;
/** Grosor (px) del marco de borde donde aparece la mano y se puede agarrar para mover
 * el pin. En el centro NO se agarra (deja libre el ícono/nivel/barra). */
const PIN_EDGE = 10;
/** Dwell: el tooltip del pin recién aparece tras dejar el mouse encima este tiempo. */
const PIN_HOVER_DELAY = 1000;

/** Base de la wiki. En LOCAL apunta a la wiki local (9002); si no, `WIKI_URL`. */
function wikiBase(): string {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return `${window.location.protocol}//${h}:9002`;
  return WIKI_URL;
}

/** Rect del canvas del juego (para clampear dentro del área jugable, sin letterbox). */
function canvasRect(): { left: number; top: number; right: number; bottom: number } {
  const c = document.querySelector('canvas');
  if (c) {
    const r = c.getBoundingClientRect();
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
}

let styleInjected = false;
function ensureStyle(): void {
  if (styleInjected) return;
  const css = `
    .skp-root {
      position: fixed; left: 0; top: 0; width: 234px; max-height: 80vh;
      display: none; flex-direction: column;
      background: linear-gradient(#1b140c, #120d07); border: 2px solid #caa24a;
      border-radius: 8px; box-shadow: 0 8px 28px rgba(0,0,0,0.6); z-index: 10000;
      font-family: ${GAME_FONT_FAMILY}; color: #ece3cf; user-select: none;
    }
    .skp-header {
      position: relative; padding: 9px 12px; text-align: center; cursor: move;
      border-bottom: 1px solid rgba(202,162,74,0.35);
    }
    .skp-title { font-size: 17px; color: #f0d68c; letter-spacing: 1px; }
    .skp-close {
      position: absolute; top: 6px; right: 9px; width: 22px; height: 22px; line-height: 20px;
      text-align: center; font-size: 15px; color: #caa24a; cursor: pointer; border-radius: 4px;
    }
    .skp-close:hover { background: rgba(202,162,74,0.15); color: #ffe9a8; }
    .skp-total {
      padding: 6px 12px; text-align: center; font-size: 14px; color: #cdbb8e;
      background: rgba(0,0,0,0.22); border-bottom: 1px solid rgba(202,162,74,0.18);
    }
    .skp-total b { color: #f2d98a; }
    .skp-body {
      flex: 1 1 auto; overflow-y: auto; padding: 8px;
      display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; align-content: start;
    }
    .skp-cell {
      position: relative; display: flex; align-items: center; justify-content: space-between;
      gap: 3px; padding: 6px 6px 9px; background: rgba(0,0,0,0.30);
      border: 1px solid #362a17; border-left-width: 3px; border-radius: 4px; overflow: hidden;
    }
    .skp-cell:hover { background: rgba(46,34,15,0.65); }
    .skp-cell-ic { flex: 0 0 auto; image-rendering: pixelated; }
    .skp-cell-lvl { flex: 0 0 auto; font-size: 16px; color: #f2d98a; }
    .skp-cell-bar {
      position: absolute; left: 3px; right: 3px; bottom: 2px; height: 3px;
      background: rgba(0,0,0,0.55); border-radius: 2px; overflow: hidden;
    }
    .skp-cell-fill { position: absolute; left: 0; top: 0; bottom: 0; width: 0%;
      background: linear-gradient(#9fe07a, #6fb44e); }
    .skp-cell-fill.max { background: linear-gradient(#f4dd93, #d9b24e); }
    .skp-cell.fav { box-shadow: inset 0 0 0 1px rgba(242,217,138,0.30); }

    /* Tooltip con toda la data + acciones (fade in/out) */
    .skp-tt {
      position: fixed; z-index: 10050; min-width: 190px; max-width: 240px;
      padding: 10px 6px; background: rgba(16,12,7,0.98); border: 1px solid #caa24a;
      border-radius: 7px; box-shadow: 0 4px 14px rgba(0,0,0,0.6);
      font-family: ${GAME_FONT_FAMILY}; color: #ece3cf;
      opacity: 0; pointer-events: none; transition: opacity 0.14s ease;
    }
    .skp-tt.show { opacity: 1; pointer-events: auto; }
    .skp-tt-name { font-size: 18px; margin-bottom: 5px; }
    .skp-tt-row { display: flex; justify-content: space-between; font-size: 15px; color: #c9bfa6; margin: 2px 0; }
    .skp-tt-row b { color: #f2d98a; }
    .skp-tt-bar { position: relative; height: 7px; margin: 6px 0 4px; background: #0a0803;
      border: 1px solid #000; border-radius: 3px; overflow: hidden; }
    .skp-tt-fill { position: absolute; left: 0; top: 0; bottom: 0; width: 0%;
      background: linear-gradient(#9fe07a, #6fb44e); }
    .skp-tt-fill.max { background: linear-gradient(#f4dd93, #d9b24e); }
    /* Acciones (i · estrella · pin) alineadas a la DERECHA. */
    .skp-tt-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; padding-top: 7px;
      border-top: 1px solid rgba(202,162,74,0.22); align-items: center; }
    .skp-tt-act { cursor: pointer; color: #9c927c; line-height: 1; }
    /* i (libro) · estrella · pin (banderín): íconos del atlas. El tamaño y el grayscale
       activo/inactivo los setea fillTooltip (por eso no hay font-size acá). */
    .skp-tt-icon { flex: 0 0 auto; transition: transform 0.1s ease; }
    .skp-tt-icon:hover { transform: scale(1.15); }

    /* Los pines flotantes ya NO son DOM: se dibujan como HUD Phaser sobre el
       canvas (ver createPinWidget) para quedar por DEBAJO de las ventanas. */
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  styleInjected = true;
}

interface SkillCell {
  cellEl: HTMLDivElement;
  lvlEl: HTMLSpanElement;
  fillEl: HTMLDivElement;
}

/** Elementos del tooltip (reusable). */
interface TooltipRefs {
  root: HTMLDivElement;
  name: HTMLDivElement;
  level: HTMLElement;
  xp: HTMLElement;
  next: HTMLElement;
  fill: HTMLDivElement;
  star: HTMLDivElement;
  pin: HTMLDivElement;
}

/**
 * Widget flotante de un skill pineado (ícono + nivel + barra), movible. Ahora es
 * un HUD Phaser: un Container en la uiCamera con scrollFactor 0. `x`/`y` son la
 * esquina sup-izq en coords de pantalla del juego (no de página). `fill` es un
 * Graphics que se redibuja al cambiar la XP.
 */
interface PinWidget {
  id: SkillId;
  container: Phaser.GameObjects.Container;
  fill: Phaser.GameObjects.Graphics;
  levelText: Phaser.GameObjects.Text;
  x: number;
  y: number;
}

export class SkillsPanel {
  private root!: HTMLDivElement;
  private body!: HTMLDivElement;
  private totalEl!: HTMLElement;
  private cells = new Map<SkillId, SkillCell>();
  private xp = new Map<SkillId, number>();
  private favorites = new Set<SkillId>();
  private pins = new Map<SkillId, PinWidget>();
  private tt!: TooltipRefs;
  private ttSkill: SkillId | null = null;
  private ttHideTimer: number | null = null;
  private scene: HudScene | null = null;
  private draggingPin: PinWidget | null = null;
  private dragDX = 0;
  private dragDY = 0;
  private pinHandlersReady = false;
  private pinHoverTimer: number | null = null;
  private onPinPointerMove?: (pointer: Phaser.Input.Pointer) => void;
  private onPinPointerUp?: () => void;
  private panelPos: { x: number; y: number } | null = null;
  private open = false;

  constructor(scene?: Phaser.Scene) {
    this.scene = (scene as HudScene) ?? null;
    ensureStyle();
    this.loadFavorites();
    this.loadPos();
    this.build(); // arma el panel DOM + tooltip (this.tt) primero…
    this.loadPins(); // …y después los pines Phaser (usan la escena + el tooltip).
  }

  private build(): void {
    this.root = document.createElement('div');
    this.root.className = 'skp-root';
    this.root.addEventListener('keydown', (e) => e.stopPropagation());

    const header = document.createElement('div');
    header.className = 'skp-header';
    const title = document.createElement('div');
    title.className = 'skp-title';
    title.textContent = 'Habilidades';
    const close = document.createElement('div');
    close.className = 'skp-close';
    close.textContent = '✕';
    close.onclick = (e) => {
      e.stopPropagation();
      this.hide();
    };
    header.appendChild(title);
    header.appendChild(close);
    this.attachHeaderDrag(header);
    this.root.appendChild(header);

    this.totalEl = document.createElement('div');
    this.totalEl.className = 'skp-total';
    this.root.appendChild(this.totalEl);

    this.body = document.createElement('div');
    this.body.className = 'skp-body';
    for (const id of ALL_SKILL_IDS) this.body.appendChild(this.buildCell(id));
    this.root.appendChild(this.body);

    document.body.appendChild(this.root);
    this.buildTooltip();
    this.updateTotal();
  }

  private buildCell(id: SkillId): HTMLDivElement {
    const def = SKILL_DEFS[id];
    const cell = document.createElement('div');
    cell.className = 'skp-cell';
    cell.style.borderLeftColor = CATEGORY_COLOR[def.category];

    const icon = document.createElement('div');
    icon.className = 'skp-cell-ic';
    if (!applyIconToDom(icon, def.icon, 26)) {
      icon.style.width = '26px';
      icon.style.height = '26px';
    }
    const lvlEl = document.createElement('span');
    lvlEl.className = 'skp-cell-lvl';
    lvlEl.textContent = '1';

    const bar = document.createElement('div');
    bar.className = 'skp-cell-bar';
    const fillEl = document.createElement('div');
    fillEl.className = 'skp-cell-fill';
    bar.appendChild(fillEl);

    cell.appendChild(icon);
    cell.appendChild(lvlEl);
    cell.appendChild(bar);

    cell.addEventListener('mouseenter', () => this.showTooltip(id, cell));
    cell.addEventListener('mouseleave', () => this.scheduleHideTooltip());

    this.cells.set(id, { cellEl: cell, lvlEl, fillEl });
    this.applyOrder(id);
    this.updateCell(id);
    return cell;
  }

  // ── Tooltip ────────────────────────────────────────────────────────────
  private buildTooltip(): void {
    const root = document.createElement('div');
    root.className = 'skp-tt';
    // Título: solo el nombre del skill (los íconos van abajo, a la derecha).
    const name = document.createElement('div');
    name.className = 'skp-tt-name';

    const mkRow = (label: string): { row: HTMLDivElement; val: HTMLElement } => {
      const row = document.createElement('div');
      row.className = 'skp-tt-row';
      const l = document.createElement('span');
      l.textContent = label;
      const val = document.createElement('b');
      row.appendChild(l);
      row.appendChild(val);
      return { row, val };
    };
    const levelRow = mkRow('Nivel');
    const xpRow = mkRow('Experiencia');
    const nextRow = mkRow('Próximo nivel');

    const bar = document.createElement('div');
    bar.className = 'skp-tt-bar';
    const fill = document.createElement('div');
    fill.className = 'skp-tt-fill';
    bar.appendChild(fill);

    // Fila de acciones a la DERECHA: i (wiki) · estrella (favorito) · pin (fijar).
    const actions = document.createElement('div');
    actions.className = 'skp-tt-actions';

    // Los 3 son ÍCONOS del atlas icons3 (no letras/emoji). info=libro/wiki,
    // star=estrella, pin=banderín (no hay chincheta en el atlas de fantasía).
    const info = document.createElement('div');
    info.className = 'skp-tt-act skp-tt-icon';
    info.title = 'Ver en la wiki';
    if (!applyIconToDom(info, 'wiki_book', 16)) info.textContent = 'i';
    info.onclick = () => {
      if (this.ttSkill) window.open(getSkillWikiUrl(wikiBase(), this.ttSkill), '_blank', 'noopener');
    };

    // Estrella (favorito). Activa/inactiva → tamaño + grayscale en fillTooltip.
    const star = document.createElement('div');
    star.className = 'skp-tt-act skp-tt-icon';
    star.title = 'Favorito (aparece primero)';
    if (!applyIconToDom(star, 'star', 16)) star.textContent = '★';
    star.onclick = () => {
      if (this.ttSkill) this.toggleFavorite(this.ttSkill);
    };

    // Pin = banderín/marcador. Activo/inactivo → tamaño + grayscale en fillTooltip.
    const pin = document.createElement('div');
    pin.className = 'skp-tt-act skp-tt-icon';
    pin.title = 'Fijar en pantalla (hasta 3, movibles)';
    if (!applyIconToDom(pin, 'pin_flag', 16)) pin.textContent = '📌';
    pin.onclick = () => {
      if (this.ttSkill) this.togglePin(this.ttSkill);
    };
    actions.appendChild(info);
    actions.appendChild(star);
    actions.appendChild(pin);

    root.appendChild(name);
    root.appendChild(levelRow.row);
    root.appendChild(bar);
    root.appendChild(xpRow.row);
    root.appendChild(nextRow.row);
    root.appendChild(actions);
    root.addEventListener('mouseenter', () => this.cancelHideTooltip());
    root.addEventListener('mouseleave', () => this.scheduleHideTooltip());
    document.body.appendChild(root);

    this.tt = {
      root,
      name,
      level: levelRow.val,
      xp: xpRow.val,
      next: nextRow.val,
      fill,
      star,
      pin,
    };
  }

  private showTooltip(id: SkillId, cell: HTMLDivElement): void {
    this.cancelHideTooltip();
    this.ttSkill = id;
    this.fillTooltip(id);
    // Posición al lado de la celda, clampeada al viewport.
    const r = cell.getBoundingClientRect();
    this.tt.root.style.left = '0px';
    this.tt.root.style.top = '0px';
    this.tt.root.classList.add('show');
    const tw = this.tt.root.offsetWidth;
    const th = this.tt.root.offsetHeight;
    let x = r.right + 8;
    if (x + tw > window.innerWidth) x = r.left - tw - 8;
    if (x < 4) x = 4;
    let y = r.top;
    if (y + th > window.innerHeight) y = window.innerHeight - th - 4;
    if (y < 4) y = 4;
    this.tt.root.style.left = `${Math.round(x)}px`;
    this.tt.root.style.top = `${Math.round(y)}px`;
  }

  /**
   * Muestra el tooltip anclado a un punto de PÁGINA (clientX/clientY), en vez de
   * al lado de una celda. Lo usan los pines Phaser al hacer hover (el pin está en
   * el canvas, así que lo anclamos a la posición del mouse). Se ubica abajo-derecha
   * del cursor y se clampa al viewport.
   */
  private showTooltipAt(id: SkillId, clientX: number, clientY: number): void {
    this.cancelHideTooltip();
    this.ttSkill = id;
    this.fillTooltip(id);
    this.tt.root.style.left = '0px';
    this.tt.root.style.top = '0px';
    this.tt.root.classList.add('show');
    const tw = this.tt.root.offsetWidth;
    const th = this.tt.root.offsetHeight;
    let x = clientX + 16;
    if (x + tw > window.innerWidth) x = clientX - tw - 16;
    if (x < 4) x = 4;
    let y = clientY + 16;
    if (y + th > window.innerHeight) y = clientY - th - 16;
    if (y < 4) y = 4;
    this.tt.root.style.left = `${Math.round(x)}px`;
    this.tt.root.style.top = `${Math.round(y)}px`;
  }

  /** Igual que showTooltipAt pero tomando el evento DOM del puntero activo de Phaser. */
  private showTooltipAtPointer(id: SkillId, pointer: Phaser.Input.Pointer): void {
    const ev = pointer.event as MouseEvent | undefined;
    if (ev && typeof ev.clientX === 'number') {
      this.showTooltipAt(id, ev.clientX, ev.clientY);
    } else {
      // Fallback: convertir coords del juego → página con el rect del canvas.
      const r = canvasRect();
      const gw = this.scene?.scale.gameSize.width || r.right - r.left || 1;
      const f = (r.right - r.left) / gw;
      this.showTooltipAt(id, r.left + pointer.x * f, r.top + pointer.y * f);
    }
  }

  private fillTooltip(id: SkillId): void {
    const def = SKILL_DEFS[id];
    const xp = this.xp.get(id) ?? 0;
    const level = getSkillLevelFromXp(xp);
    const prog = getXpToNextLevel(xp);
    this.tt.name.textContent = def.name;
    this.tt.name.style.color = CATEGORY_COLOR[def.category];
    this.tt.level.textContent = `${level} / ${SKILL_MAX_LEVEL}`;
    this.tt.xp.textContent = formatThousands(xp);
    const isMax = level >= SKILL_MAX_LEVEL;
    this.tt.next.textContent = isMax ? '—' : `${formatThousands(prog.totalForNext)} (faltan ${formatThousands(prog.xpToNext)})`;
    const frac = isMax || prog.xpForSpan <= 0 ? 1 : Math.max(0, Math.min(1, prog.xpIntoLevel / prog.xpForSpan));
    this.tt.fill.style.width = `${Math.round(frac * 100)}%`;
    this.tt.fill.classList.toggle('max', isMax);
    // Estrella (atlas): activa = plena + un poco más grande; inactiva = grayscale + chica.
    const favOn = this.favorites.has(id);
    applyIconToDom(this.tt.star, 'star', favOn ? 18 : 15);
    this.tt.star.style.filter = favOn ? 'none' : 'grayscale(1)';
    this.tt.star.style.opacity = favOn ? '1' : '0.5';
    // Pin (banderín del atlas): mismo criterio activo/inactivo.
    const pinOn = this.pins.has(id);
    applyIconToDom(this.tt.pin, 'pin_flag', pinOn ? 18 : 15);
    this.tt.pin.style.filter = pinOn ? 'none' : 'grayscale(1)';
    this.tt.pin.style.opacity = pinOn ? '1' : '0.5';
  }

  private scheduleHideTooltip(): void {
    this.cancelHideTooltip();
    this.ttHideTimer = window.setTimeout(() => {
      this.tt.root.classList.remove('show');
      this.ttSkill = null;
    }, 160);
  }

  private cancelHideTooltip(): void {
    if (this.ttHideTimer !== null) {
      window.clearTimeout(this.ttHideTimer);
      this.ttHideTimer = null;
    }
  }

  private hideTooltipNow(): void {
    this.cancelHideTooltip();
    this.tt.root.classList.remove('show');
    this.ttSkill = null;
  }

  // ── Celdas / total ─────────────────────────────────────────────────────
  private updateCell(id: SkillId): void {
    const cell = this.cells.get(id);
    if (!cell) return;
    const xp = this.xp.get(id) ?? 0;
    const level = getSkillLevelFromXp(xp);
    cell.lvlEl.textContent = String(level);
    const prog = getXpToNextLevel(xp);
    const frac =
      level >= SKILL_MAX_LEVEL || prog.xpForSpan <= 0
        ? 1
        : Math.max(0, Math.min(1, prog.xpIntoLevel / prog.xpForSpan));
    cell.fillEl.style.width = `${Math.round(frac * 100)}%`;
    cell.fillEl.classList.toggle('max', level >= SKILL_MAX_LEVEL);
    if (this.ttSkill === id) this.fillTooltip(id); // refrescar tooltip abierto
    this.updatePin(id);
  }

  private updateTotal(): void {
    let total = 0;
    for (const id of ALL_SKILL_IDS) total += getSkillLevelFromXp(this.xp.get(id) ?? 0);
    this.totalEl.innerHTML = `Nivel total: <b>${formatThousands(total)}</b>`;
  }

  public applySkills(entries: Array<{ skillId: string; experience: number; level: number }>): void {
    for (const e of entries) {
      if (e.skillId in SKILL_DEFS) {
        this.xp.set(e.skillId as SkillId, e.experience);
        this.updateCell(e.skillId as SkillId);
      }
    }
    this.updateTotal();
  }

  public setSkillXp(skillId: string, experience: number): void {
    if (!(skillId in SKILL_DEFS)) return;
    this.xp.set(skillId as SkillId, experience);
    this.updateCell(skillId as SkillId);
    this.updateTotal();
  }

  // ── Favoritos (orden) ──────────────────────────────────────────────────
  private orderOf(id: SkillId): number {
    const base = ALL_SKILL_IDS.indexOf(id);
    return this.favorites.has(id) ? base - 1000 : base;
  }

  private applyOrder(id: SkillId): void {
    const cell = this.cells.get(id);
    if (!cell) return;
    cell.cellEl.style.order = String(this.orderOf(id));
    cell.cellEl.classList.toggle('fav', this.favorites.has(id));
  }

  private toggleFavorite(id: SkillId): void {
    if (this.favorites.has(id)) this.favorites.delete(id);
    else this.favorites.add(id);
    this.applyOrder(id);
    if (this.ttSkill === id) this.tt.star.classList.toggle('on', this.favorites.has(id));
    this.saveFavorites();
  }

  private loadFavorites(): void {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      if (!raw) return;
      for (const s of JSON.parse(raw) as string[]) if (s in SKILL_DEFS) this.favorites.add(s as SkillId);
    } catch {
      /* ignore */
    }
  }

  private saveFavorites(): void {
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify([...this.favorites]));
    } catch {
      /* ignore */
    }
  }

  // ── Pines (HUD Phaser sobre el canvas, como el minimapa) ───────────────
  private togglePin(id: SkillId): void {
    if (this.pins.has(id)) {
      this.removePinWidget(id);
    } else {
      if (this.pins.size >= MAX_PINS) return;
      // Posición default: apilados en la esquina sup-izq (coords de pantalla del juego).
      this.createPinWidget(id, 12, 60 + this.pins.size * (PIN_H + 8));
    }
    if (this.ttSkill === id) this.fillTooltip(id); // refresca el estado activo del pin en el tooltip
    this.savePins();
  }

  /** Registra (una vez) los handlers de drag a nivel de escena. */
  private ensurePinDragHandlers(): void {
    if (this.pinHandlersReady || !this.scene) return;
    this.pinHandlersReady = true;
    this.onPinPointerMove = (pointer: Phaser.Input.Pointer): void => {
      if (!this.draggingPin) return;
      this.placePin(this.draggingPin, pointer.x - this.dragDX, pointer.y - this.dragDY);
    };
    this.onPinPointerUp = (): void => {
      if (!this.draggingPin) return;
      this.draggingPin = null;
      this.scene?.input.setDefaultCursor('default');
      this.savePins();
    };
    this.scene.input.on('pointermove', this.onPinPointerMove);
    this.scene.input.on('pointerup', this.onPinPointerUp);
  }

  private createPinWidget(id: SkillId, x: number, y: number): void {
    const scene = this.scene;
    if (!scene) return;
    this.ensurePinDragHandlers();
    const def = SKILL_DEFS[id];
    const innerW = PIN_W - 2 * PIN_PAD;
    const barTop = PIN_H - 6 - PIN_BAR_H;

    // Fondo: caja redondeada + borde + fondo de la barra (estático).
    const bg = scene.add.graphics();
    bg.fillStyle(PIN_BG_COLOR, PIN_BG_ALPHA);
    bg.fillRoundedRect(0, 0, PIN_W, PIN_H, PIN_RADIUS);
    bg.lineStyle(1, PIN_BORDER_COLOR, 1);
    bg.strokeRoundedRect(0.5, 0.5, PIN_W - 1, PIN_H - 1, PIN_RADIUS);
    bg.fillStyle(PIN_BAR_BG_COLOR, 1);
    bg.fillRect(PIN_PAD, barTop, innerW, PIN_BAR_H);

    // Ícono del skill (24px, centrado en la zona izquierda de la fila superior).
    const icon = createUIIcon(scene, PIN_PAD + 12, 18, def.icon, 24);
    // Nivel X/99 a la derecha de la fila superior.
    const levelText = scene.add
      .text(PIN_W - PIN_PAD, 18, `1/${SKILL_MAX_LEVEL}`, {
        fontFamily: GAME_FONT_FAMILY,
        fontSize: '15px',
        color: PIN_LEVEL_COLOR,
        resolution: 2,
      })
      .setOrigin(1, 0.5);
    // Relleno de la barra (dinámico: se redibuja al cambiar la XP).
    const fill = scene.add.graphics();

    const children: Phaser.GameObjects.GameObject[] = [bg];
    if (icon) children.push(icon);
    children.push(levelText, fill);

    const container = scene.add.container(x, y, children);
    container.setDepth(PIN_DEPTH).setScrollFactor(0).setSize(PIN_W, PIN_H);
    container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, PIN_W, PIN_H),
      Phaser.Geom.Rectangle.Contains
    );
    // La cámara principal lo ignora (queda sólo en la uiCamera, depth ≥ 800).
    scene.configureObjectCamera(container);

    const widget: PinWidget = { id, container, fill, levelText, x, y };

    // Hover → tooltip con DWELL: recién aparece si dejás el mouse ~1s encima. La mano
    // (cursor grab) sólo aparece en el MARCO de borde del box; en el centro queda default.
    container.on('pointerover', (pointer: Phaser.Input.Pointer) => {
      if (this.draggingPin) return;
      this.updatePinCursor(widget, pointer);
      this.cancelPinHover();
      this.pinHoverTimer = window.setTimeout(() => {
        this.pinHoverTimer = null;
        // Al dispararse: si seguimos sobre el pin (no arrastrando), mostrar el
        // tooltip en la posición ACTUAL del mouse.
        if (!this.draggingPin) this.showTooltipAtPointer(id, scene.input.activePointer);
      }, PIN_HOVER_DELAY);
    });
    container.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.draggingPin) return;
      this.updatePinCursor(widget, pointer);
    });
    container.on('pointerout', () => {
      this.cancelPinHover();
      if (!this.draggingPin) scene.input.setDefaultCursor('default');
      this.scheduleHideTooltip();
    });
    container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Sólo se agarra para mover desde el BORDE. Un click en el centro no arrastra.
      if (!this.isPinEdge(widget, pointer)) return;
      this.cancelPinHover();
      this.draggingPin = widget;
      this.dragDX = pointer.x - widget.x;
      this.dragDY = pointer.y - widget.y;
      this.hideTooltipNow();
      scene.input.setDefaultCursor('grabbing');
    });

    this.pins.set(id, widget);
    this.placePin(widget, x, y);
    this.updatePin(id);
  }

  /**
   * True si el puntero cae dentro del MARCO de borde del pin (a menos de PIN_EDGE de
   * cualquier lado). El container es scrollFactor 0, así que las coords locales son
   * `pointer - widget.pos` (misma base que dragDX/dragDY).
   */
  private isPinEdge(widget: PinWidget, pointer: Phaser.Input.Pointer): boolean {
    const lx = pointer.x - widget.x;
    const ly = pointer.y - widget.y;
    if (lx < 0 || lx > PIN_W || ly < 0 || ly > PIN_H) return false;
    return lx < PIN_EDGE || lx > PIN_W - PIN_EDGE || ly < PIN_EDGE || ly > PIN_H - PIN_EDGE;
  }

  /** Mano (grab) en el borde, cursor normal en el centro. */
  private updatePinCursor(widget: PinWidget, pointer: Phaser.Input.Pointer): void {
    if (!this.scene) return;
    this.scene.input.setDefaultCursor(this.isPinEdge(widget, pointer) ? 'grab' : 'default');
  }

  /** Cancela el timer de dwell del tooltip del pin (si hay uno pendiente). */
  private cancelPinHover(): void {
    if (this.pinHoverTimer !== null) {
      window.clearTimeout(this.pinHoverTimer);
      this.pinHoverTimer = null;
    }
  }

  /** Redibuja el relleno de la barra de XP del pin (verde, dorado si nivel máx). */
  private drawPinFill(widget: PinWidget, frac: number, isMax: boolean): void {
    const innerW = PIN_W - 2 * PIN_PAD;
    const barTop = PIN_H - 6 - PIN_BAR_H;
    const w = Math.max(0, (innerW - 2) * Math.max(0, Math.min(1, frac)));
    widget.fill.clear();
    if (w <= 0) return;
    widget.fill.fillStyle(isMax ? PIN_FILL_MAX_COLOR : PIN_FILL_COLOR, 1);
    widget.fill.fillRect(PIN_PAD + 1, barTop + 1, w, PIN_BAR_H - 2);
  }

  /** Ubica el pin clampeado DENTRO de la pantalla del juego (viewport de la cámara). */
  private placePin(widget: PinWidget, x: number, y: number): void {
    // Al arrastrar: si el pin queda cerca de otro, pegar su borde al del otro.
    if (this.draggingPin === widget) {
      const snapped = this.snapToPins(widget, x, y);
      x = snapped.x;
      y = snapped.y;
    }
    const cam = this.scene?.cameras.main;
    const maxX = (cam?.width ?? window.innerWidth) - PIN_W;
    const maxY = (cam?.height ?? window.innerHeight) - PIN_H;
    widget.x = Phaser.Math.Clamp(x, 0, Math.max(0, maxX));
    widget.y = Phaser.Math.Clamp(y, 0, Math.max(0, maxY));
    widget.container.setPosition(widget.x, widget.y);
  }

  /**
   * Snap magnético entre pines: dado el pin `widget` en la posición propuesta
   * `(x, y)`, si otro pin está a menos de `PIN_SNAP_PX`, pega el borde más cercano
   * al del otro y alinea el eje cruzado (para que queden al ras, tipo bloques).
   * Todos los pines miden PIN_W×PIN_H, así que el pegado es exacto.
   */
  private snapToPins(widget: PinWidget, x: number, y: number): { x: number; y: number } {
    const w = PIN_W;
    const h = PIN_H;
    const xCands: number[] = [];
    const yCands: number[] = [];
    for (const other of this.pins.values()) {
      if (other === widget) continue;
      const ox = other.x;
      const oy = other.y;
      const ow = PIN_W;
      const oh = PIN_H;
      const nearV = y < oy + oh + PIN_SNAP_PX && y + h > oy - PIN_SNAP_PX;
      const nearH = x < ox + ow + PIN_SNAP_PX && x + w > ox - PIN_SNAP_PX;
      if (nearV) {
        xCands.push(ox + ow, ox - w); // pegar a la derecha / a la izquierda del otro
        yCands.push(oy, oy + oh - h); // alinear topes / bases
      }
      if (nearH) {
        yCands.push(oy + oh, oy - h); // pegar abajo / arriba del otro
        xCands.push(ox, ox + ow - w); // alinear izquierdas / derechas
      }
    }
    const snap = (v: number, cands: number[]): number => {
      let best = v;
      let bestDist = PIN_SNAP_PX + 1;
      for (const c of cands) {
        const d = Math.abs(v - c);
        if (d < bestDist) {
          bestDist = d;
          best = c;
        }
      }
      return bestDist <= PIN_SNAP_PX ? best : v;
    };
    return { x: snap(x, xCands), y: snap(y, yCands) };
  }

  private removePinWidget(id: SkillId): void {
    const w = this.pins.get(id);
    if (!w) return;
    this.cancelPinHover();
    w.container.destroy(); // destruye también sus hijos (bg/ícono/texto/fill)
    this.pins.delete(id);
  }

  private updatePin(id: SkillId): void {
    const w = this.pins.get(id);
    if (!w) return;
    const xp = this.xp.get(id) ?? 0;
    const level = getSkillLevelFromXp(xp);
    const prog = getXpToNextLevel(xp);
    const isMax = level >= SKILL_MAX_LEVEL;
    w.levelText.setText(`${level}/${SKILL_MAX_LEVEL}`);
    const frac = isMax || prog.xpForSpan <= 0 ? 1 : prog.xpIntoLevel / prog.xpForSpan;
    this.drawPinFill(w, frac, isMax);
  }

  private loadPins(): void {
    if (!this.scene) return;
    try {
      const raw = localStorage.getItem(PIN_KEY);
      if (!raw) return;
      for (const p of JSON.parse(raw) as Array<{ skillId: string; x: number; y: number }>) {
        if (this.pins.size >= MAX_PINS) break;
        if (p.skillId in SKILL_DEFS && !this.pins.has(p.skillId as SkillId)) {
          this.createPinWidget(p.skillId as SkillId, Number(p.x) || 12, Number(p.y) || 60);
        }
      }
    } catch {
      /* ignore */
    }
  }

  private savePins(): void {
    try {
      const arr = [...this.pins.entries()].map(([skillId, w]) => ({ skillId, x: w.x, y: w.y }));
      localStorage.setItem(PIN_KEY, JSON.stringify(arr));
    } catch {
      /* ignore */
    }
  }

  // ── Ventana: drag por header + clamp al canvas + persistencia ──────────
  private attachHeaderDrag(header: HTMLDivElement): void {
    header.addEventListener('mousedown', (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains('skp-close')) return;
      e.preventDefault();
      const sx = e.clientX;
      const sy = e.clientY;
      const r = this.root.getBoundingClientRect();
      const bx = r.left;
      const by = r.top;
      const move = (ev: MouseEvent): void => this.placeRoot(bx + (ev.clientX - sx), by + (ev.clientY - sy));
      const up = (): void => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        this.savePos();
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    });
  }

  /** Ubica el panel clampeado DENTRO del canvas del juego (no en las barras negras). */
  private placeRoot(x: number, y: number): void {
    const r = canvasRect();
    const w = this.root.offsetWidth || 234;
    const h = this.root.offsetHeight || 420;
    const cx = Math.max(r.left, Math.min(x, r.right - w));
    const cy = Math.max(r.top, Math.min(y, r.bottom - h));
    this.panelPos = { x: cx, y: cy };
    this.root.style.left = `${Math.round(cx)}px`;
    this.root.style.top = `${Math.round(cy)}px`;
  }

  private loadPos(): void {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) {
        const p = JSON.parse(raw) as { x: number; y: number };
        if (typeof p.x === 'number' && typeof p.y === 'number') this.panelPos = p;
      }
    } catch {
      /* ignore */
    }
  }

  private savePos(): void {
    try {
      if (this.panelPos) localStorage.setItem(POS_KEY, JSON.stringify(this.panelPos));
    } catch {
      /* ignore */
    }
  }

  // ── Toggle ─────────────────────────────────────────────────────────────
  public toggle(): boolean {
    this.open = !this.open;
    this.root.style.display = this.open ? 'flex' : 'none';
    if (this.open) {
      // Posición default: esquina superior derecha dentro del canvas.
      if (this.panelPos) this.placeRoot(this.panelPos.x, this.panelPos.y);
      else {
        const r = canvasRect();
        this.placeRoot(r.right - (this.root.offsetWidth || 234) - 16, r.top + 54);
      }
    }
    return this.open;
  }

  public isOpen(): boolean {
    return this.open;
  }

  public hide(): void {
    this.open = false;
    this.root.style.display = 'none';
  }

  public destroy(): void {
    this.cancelPinHover();
    this.root.remove();
    this.tt.root.remove();
    for (const w of this.pins.values()) w.container.destroy();
    this.pins.clear();
    if (this.scene) {
      if (this.onPinPointerMove) this.scene.input.off('pointermove', this.onPinPointerMove);
      if (this.onPinPointerUp) this.scene.input.off('pointerup', this.onPinPointerUp);
    }
  }
}
